/**
 * Course Viewer Page
 * Redirects to course content in new tab with authentication
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import API_URL from '../config/api';

const CourseViewer = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [hasAccess, setHasAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const response = await api.get(`/courses/check-access/${courseId}`);
        setHasAccess(response.data.hasAccess);
        
        if (response.data.hasAccess) {
          // User has access, verify course file exists before opening
          try {
            const token = localStorage.getItem('token');
            const courseUrl = `${API_URL}/api/courses/${courseId}/index.html?token=${token}`;
            
            // Check if the course file exists by making a HEAD request
            const fileCheck = await fetch(courseUrl, { 
              method: 'HEAD',
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (!fileCheck.ok) {
              // Course file not found
              if (fileCheck.status === 404) {
                setError(`Course materials for ${courseId.toUpperCase()} are not available yet. Please contact your instructor.`);
              } else {
                setError('Unable to load course materials. Please try again later.');
              }
              setLoading(false);
              return;
            }
            
            // File exists, open in new tab
            const newWindow = window.open(courseUrl, '_blank');
            
            // Check if popup was blocked
            if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
              setError('Please allow popups for this site to view course materials.');
              setLoading(false);
              return;
            }
            
            // Redirect back to teaching page after opening course
            setTimeout(() => {
              navigate('/teaching');
            }, 1000);
          } catch (windowError) {
            console.error('Error opening course window:', windowError);
            setError('Unable to open course materials. Please check your browser settings.');
            setLoading(false);
          }
        } else {
          // No access, redirect after showing message
          setTimeout(() => navigate('/teaching'), 3000);
        }
      } catch (error) {
        console.error('Error checking access:', error);
        
        // Better error message based on error type
        if (error.response?.status === 403) {
          setError('You do not have permission to access this course.');
        } else if (error.response?.status === 404) {
          setError(`Course ${courseId.toUpperCase()} not found.`);
        } else {
          setError('Error checking course access. Please try again later.');
        }
        
        setHasAccess(false);
        setLoading(false);
      }
    };

    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/course/${courseId}` } });
      return;
    }

    checkAccess();
  }, [isAuthenticated, courseId, navigate]);

  if (loading) {
    return (
      <Container>
        <LoadingMessage>
          <h2>Checking course access...</h2>
          <p>Please wait...</p>
        </LoadingMessage>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>
          <h2>Course Not Available</h2>
          <p>{error}</p>
          <BackButton onClick={() => navigate('/teaching')}>
            Return to Teaching Page
          </BackButton>
        </ErrorMessage>
      </Container>
    );
  }

  if (hasAccess === false) {
    return (
      <Container>
        <ErrorMessage>
          <h2>Access Denied</h2>
          <p>You do not have access to this course.</p>
          <p>Redirecting to teaching page...</p>
        </ErrorMessage>
      </Container>
    );
  }

  return (
    <Container>
      <SuccessMessage>
        <h2>Opening Course...</h2>
        <p>Your course is opening in a new tab.</p>
        <p>Returning to teaching page...</p>
      </SuccessMessage>
    </Container>
  );
};


const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const LoadingMessage = styled.div`
  text-align: center;
  
  h2 {
    font-size: 2rem;
    color: var(--text-primary);
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.125rem;
    color: var(--text-secondary);
  }
`;

const SuccessMessage = styled.div`
  text-align: center;
  
  h2 {
    font-size: 2rem;
    color: var(--accent-primary);
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.125rem;
    color: var(--text-secondary);
    margin-bottom: 0.5rem;
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  color: var(--text-primary);

  h2 {
    font-size: 2rem;
    margin-bottom: 1rem;
    color: var(--accent-secondary);
  }

  p {
    font-size: 1.125rem;
    color: var(--text-secondary);
    margin-bottom: 1.5rem;
  }
`;

const BackButton = styled.button`
  background: var(--accent-primary);
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--accent-hover);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(74, 158, 255, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

export default CourseViewer;

